/*
 * Copyright (c) 2018 teamapps.org (see code comments for author's name)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import * as $ from "jquery";
import {UiComponent} from "./UiComponent";
import {UiComponentConfig} from "../generated/UiComponentConfig";
import {TeamAppsUiContext} from "./TeamAppsUiContext";
import {UiVerticalLayoutConfig} from "../generated/UiVerticalLayoutConfig";
import {TeamAppsUiComponentRegistry} from "./TeamAppsUiComponentRegistry";

/**
 * @author Yann Massard (yamass@gmail.com)
 */

export class UiVerticalLayout extends UiComponent<UiVerticalLayoutConfig> {
	private $verticalLayout: JQuery;
	private children: UiComponent<UiComponentConfig>[] = [];

	constructor(config: UiVerticalLayoutConfig, context: TeamAppsUiContext) {
		super(config, context);

		this.$verticalLayout = $('<div id="' + config.id + '" class="UiVerticalLayout"></div>');

		if (config.components) {
			for (let i = 0; i < config.components.length; i++) {
				this.addComponent(config.components[i]);
			}
		}
	}

	public getMainDomElement(): JQuery {
		return this.$verticalLayout;
	}

	protected onAttachedToDom() {
		this.children.forEach(c => c.attachedToDom = true);
	}

	public onResize(): void {
		this.children.forEach(c => c.reLayout());
	}

	public destroy(): void {
		this.children.forEach(c => c.destroy());
	}

	public addComponent(childConfig: UiComponentConfig) {
		const $childWrapper = $('<div class="vertical-layout-child-wrapper" style="' + (this._config.fixedChildHeight ? 'height:' + this._config.fixedChildHeight + 'px' : '') + '">').appendTo(this.$verticalLayout);
		let childComponent = this._context.createComponent(childConfig);
		childComponent.getMainDomElement().appendTo($childWrapper);
		this.children.push(childComponent);
		childComponent.attachedToDom = this.attachedToDom;
	}

	public removeComponent(componentId: string) {
		let childrenToRemove = this.children.filter(c => c.getId() === componentId);
		this.children = this.children.filter(c => c.getId() !== componentId);
		childrenToRemove.forEach((c, index) => {
			let $childWrapper = c.getMainDomElement().closest(".vertical-layout-child-wrapper");
			c.destroy();
			$childWrapper.remove();
		});
	}
}

TeamAppsUiComponentRegistry.registerComponentClass("UiVerticalLayout", UiVerticalLayout);